import userImage from "assets/icons/kangarooUser.svg";
import logo from "assets/icons/logo.svg";
import {
  FileText,
  Globe,
  Home,
  LogOut,
  LucideTruck,
  ReceiptPoundSterlingIcon,
  Settings,
  SortAsc,
  Upload,
  User,
  Users,
} from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { USER_EMAIL, USER_NAME, USER_ROLE } from "utils/constant";
import Button from "./Button";
import MenuItem from "./MenuItem";
import { useTranslation } from "react-i18next";
import useLanguage from "hooks/useLanguage";

interface MenuProps {
  isMenuOpen: boolean;
  closeMenu: () => void;
}

const Menu = ({ isMenuOpen, closeMenu }: MenuProps) => {
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userRole, setUserRole] = useState("");

  // Use the language hook for mobile menu
  const { toggleLanguage, alternativeLanguage, alternativeLanguageLabel } =
    useLanguage();

  // Move menu definitions inside useMemo to recreate when language changes
  const adminMenuItems = useMemo(
    () => [
      {
        icon: <Home size={15} />,
        label: t("menu.adminDashboard"),
        url: "/admin-dashboard",
      },
      {
        icon: <Upload size={15} />,
        label: t("menu.orderImport"),
        url: "/order-import",
      },
      {
        icon: <Users size={15} />,
        label: t("menu.userManagement"),
        url: "/user-management",
      },
      {
        icon: <Home size={15} />,
        label: t("menu.accountantDashboard"),
        url: "/accountant-dashboard",
      },
      {
        icon: <Home size={15} />,
        label: t("menu.operatorDashboard"),
        url: "/operator-dashboard",
      },
      {
        icon: <Home size={15} />,
        label: t("menu.payment"),
        url: "/payment",
      },
      {
        icon: <Settings size={15} />,
        label: t("menu.configuration"),
        url: "/configuration",
      },
      {
        icon: <FileText size={15} />,
        label: t("menu.inventory"),
        url: "/inventory",
      },
      {
        icon: <Users size={15} />,
        label: t("menu.drivers"),
        url: "/drivers",
      },
      {
        icon: <LucideTruck size={15} />,
        label: t("menu.merchants"),
        url: "/merchants",
      },
      {
        icon: <SortAsc size={15} />,
        label: t("menu.routeManagement"),
        url: "/sort-order",
      },
    ],
    [t]
  );

  const merchantMenuItems = useMemo(
    () => [
      {
        icon: <Home size={15} />,
        label: t("menu.dashboard"),
        url: "/merchant-dashboard",
      },
      {
        icon: <FileText size={15} />,
        label: t("menu.myInventory"),
        url: "/my-inventory",
      },
      {
        icon: <ReceiptPoundSterlingIcon size={15} />,
        label: t("menu.financialReports"),
        url: "/financial-report",
      },
      {
        icon: <Settings size={15} />,
        label: t("menu.profile"),
        url: "/profile-management",
      },
    ],
    [t]
  );

  const operatorMenuItems = useMemo(
    () => [
      {
        icon: <Settings size={15} />,
        label: t("menu.dashboard"),
        url: "/operator-dashboard",
      },
      {
        icon: <Upload size={15} />,
        label: t("menu.orderImport"),
        url: "/order-import",
      },
      {
        icon: <SortAsc size={15} />,
        label: t("menu.routeManagement"),
        url: "/sort-order",
      },
    ],
    [t]
  );

  const accountantMenuItems = useMemo(
    () => [
      {
        icon: <FileText size={15} />,
        label: t("menu.dashboard"),
        url: "/accountant-dashboard",
      },
      {
        icon: <FileText size={15} />,
        label: t("menu.payment"),
        url: "/accountant-payment",
      },
      {
        icon: <Users size={15} />,
        label: t("menu.drivers"),
        url: "/drivers",
      },
      {
        icon: <LucideTruck size={15} />,
        label: t("menu.merchants"),
        url: "/merchants",
      },
    ],
    [t]
  );

  const authMenuItems = useMemo(
    () => [
      {
        icon: <User size={15} />,
        label: t("common.login"),
        url: "/",
      },
    ],
    [t]
  );

  // Initial setup - get user data from localStorage
  useEffect(() => {
    const role = localStorage.getItem(USER_ROLE);
    const name = localStorage.getItem(USER_NAME) || "";
    const email = localStorage.getItem(USER_EMAIL) || "";

    setEmail(email);
    setName(name);
    setUserRole(role || "");
  }, []);

  // Update menu items when role or language changes
  useEffect(() => {
    if (userRole) {
      switch (userRole) {
        case "admin":
          setMenuItems(adminMenuItems);
          break;
        case "merchant":
          setMenuItems(merchantMenuItems);
          break;
        case "operator":
          setMenuItems(operatorMenuItems);
          break;
        case "accountant":
          setMenuItems(accountantMenuItems);
          break;
        default:
          setMenuItems(authMenuItems);
      }
    } else {
      setMenuItems(authMenuItems);
    }
  }, [
    userRole,
    adminMenuItems,
    merchantMenuItems,
    operatorMenuItems,
    accountantMenuItems,
    authMenuItems,
  ]);

  const handleLogout = () => {
    localStorage.clear();
    localStorage.removeItem(USER_ROLE);
    localStorage.removeItem(USER_NAME);
    localStorage.removeItem(USER_EMAIL);
    window.location.href = "/";
  };

  return (
    <>
      {/* Sidebar for Desktop and Mobile */}
      <div
        className={`
          fixed inset-y-0 left-0 z-40 w-72 md:w-1/6 bg-white transform transition-all duration-300 ease-in-out shadow-2xl md:shadow-none
          md:relative md:translate-x-0
          ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}
          flex flex-col justify-between items-start px-4 py-6 border-r border-gray-200
        `}
      >
        <div className="flex flex-col gap-6 w-full">
          <div
            className="w-[120px] h-8"
            style={{
              backgroundImage: `url(${logo})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          ></div>

          {/* Language Switcher - Mobile Only */}
          <div className="md:hidden">
            <button
              onClick={() => {
                toggleLanguage();
                closeMenu();
              }}
              className="flex items-center gap-3 w-full px-3 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 rounded-lg border border-gray-200 hover:border-gray-300"
              title={`Switch to ${alternativeLanguageLabel}`}
            >
              <Globe size={16} />
              <span className="text-sm font-medium">
                {t("common.switchTo", "Switch to")} {alternativeLanguage}
              </span>
            </button>
          </div>

          <div className="flex flex-col gap-1 w-full">
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                icon={item.icon}
                label={item.label}
                url={item.url}
                onClick={closeMenu}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-3 items-center p-3 bg-gray-50 rounded-lg border">
            <img
              src={userImage}
              alt="kangaroo user"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white shadow-sm"
            />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-medium text-gray-800 truncate">
                {name}
              </span>
              <span className="text-xs text-gray-500 truncate">{email}</span>
            </div>
          </div>
          <Button
            type="submit"
            onClick={handleLogout}
            fullWidth
            icon={<LogOut size={16} />}
            className="text-red-600 border-red-600 hover:bg-red-50 hover:border-red-700 text-sm font-medium transition-all duration-200"
            variant="outlined"
          >
            {t("menu.logout")}
          </Button>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
          }}
        />
      )}
    </>
  );
};

export default Menu;
